/* eslint-disable class-methods-use-this */
import { logger } from '@app/logger';
import { transformInboxMessage } from '@core/mappers/inbox/transformInboxMessage';
import {
    MessageRequestStatus,
    MessagesRequest,
} from '@store/modules/ui/inbox/types';

import {
    InboxAction,
    InboxMethod,
} from './_private/enums';
import {
    InboxCountResponse,
    InboxMessagesResponse,
    InboxParams,
} from './_private/types';
import { loadScript } from '@superbet-group/web.lib.utilities';

export class InboxService {
    /**
     * Loads scripts needed for initialization of XtremePush.
     * XtremePush doesn't have method that listens when XtremePush is completely loaded, so this interval check is required
     */
    async loadXtremePush(): Promise<boolean> {
        if (isXtremePushLoaded()) {
            return true;
        }

        await loadScript(`${window.location.origin}/static/js/xtremePush-1_0_3.js`, 'xtremePush');
        return new Promise((resolve) => {
            const xpOnLoad = setInterval(() => {
                if (isXtremePushLoaded()) {
                    clearInterval(xpOnLoad);
                    resolve(true);
                }
            }, 50);
            setTimeout(() => {
                clearInterval(xpOnLoad);
                resolve(false);
            }, 5000);
        });
    }

    setInboxUserId(userId: number | string): Promise<boolean> {
        return new Promise((resolve) => {
            if (isXtremePushLoaded()) {
                window.xtremePush(
                    InboxMethod.Set,
                    InboxAction.UserId,
                    userId,
                    () => { resolve(true); },
                    (error: Error) => {
                        logger.error(new Error(`Error on setInboxUserId: ${error}`));
                        resolve(false);
                    },
                );
            } else {
                resolve(false);
            }
        });
    }

    fetchNumberOfNewMessages(): Promise<number> {
        return new Promise((resolve) => {
            if (isXtremePushLoaded()) {
                window.xtremePush(
                    InboxMethod.Inbox,
                    InboxAction.Count,
                    { opened: 0 } as InboxParams,
                    (response: InboxCountResponse) => {
                        resolve(response.count);
                    },
                    (error: Error) => {
                        logger.error(new Error(`Error on fetchNumberOfNewMessages: ${error}`));
                        resolve(0);
                    },
                );
            } else {
                resolve(0);
            }
        });
    }

    fetchMessages(): Promise<MessageRequestStatus> {
        return new Promise((resolve) => {
            if (isXtremePushLoaded()) {
                window.xtremePush(
                    InboxMethod.Inbox,
                    InboxAction.Messages,
                    null,
                    (response: InboxMessagesResponse) => {
                        const messages = response.items.map(transformInboxMessage);
                        resolve({
                            state: MessagesRequest.Success,
                            response: messages,
                        });
                    },
                    (error: Error) => {
                        logger.error(new Error(`Error on fetchMessages: ${error}`));
                        resolve({ state: MessagesRequest.Error });
                    },
                );
            } else {
                resolve({ state: MessagesRequest.LoadFail });
            }
        });
    }

    sendMessageAction(messageList: InboxParams[]): Promise<number> {
        return new Promise((resolve) => {
            if (isXtremePushLoaded()) {
                window.xtremePush(
                    InboxMethod.Inbox,
                    InboxAction.SetAction,
                    messageList,
                    () => { resolve(0); },
                    (error: Error) => {
                        logger.error(new Error(`Error on message.action: ${error}`));
                        resolve(messageList.length);
                    },
                );
            } else {
                resolve(messageList.length);
            }
        });
    }
}

export function isXtremePushLoaded(): boolean {
    return window.xtremePush !== undefined
        && window.xtremePush() !== undefined;
}

export const inboxService = new InboxService();
